import { motion } from "framer-motion";
import { ArrowRight, Shield, CreditCard, Clock, Sparkles } from "lucide-react";
import SEO from "../components/SEO";

export default function Payroll() {
    return (
        <div className="min-h-screen bg-base text-primary">
            <SEO
                title="Best Payroll Software for Small Business | Automated HR & Salary Management"
                description="blinqbooks Payroll is the best payroll software for small business in 2026. Automated salary distribution, tax compliance, direct deposits, time tracking & benefits management. Run payroll in minutes."
            />

            {/* Hero Section */}
            <section className="py-20 px-4 max-w-7xl mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 text-xs font-bold uppercase mb-6">
                            <Sparkles className="w-4 h-4" />
                            HR & Payroll Automation
                        </div>
                        <h1 className="text-4xl md:text-6xl font-bold mb-6 text-primary dark:text-white leading-tight">
                            Happy Teams, <br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 to-blue-500">Perfectly Paid</span>
                        </h1>
                        <p className="text-lg text-secondary dark:text-gray-300 mb-8 max-w-lg leading-relaxed">
                            Run payroll in minutes. Automated tax withholdings, direct deposits, and compliance reporting—all handled by our intelligent engine.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4">
                            <button className="btn-primary w-full sm:w-auto px-8 py-4 shadow-lg text-lg">
                                Set Up Payroll
                            </button>
                            <button className="btn-secondary w-full sm:w-auto px-8 py-4 shadow-sm text-lg">
                                How it Works
                            </button>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8 }}
                        className="relative"
                    >
                        <div className="absolute inset-0 bg-indigo-500/10 blur-[100px] rounded-full"></div>
                        <div className="relative bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl rounded-2xl p-6">
                            <div className="flex items-center justify-between mb-8">
                                <h4 className="font-bold dark:text-white">Pay Cycle: Dec 2025</h4>
                                <span className="px-3 py-1 bg-green-100 dark:bg-green-900/30 rounded-full text-green-600 text-xs font-bold uppercase">Processing</span>
                            </div>
                            <div className="space-y-4">
                                {[
                                    { name: "John Doe", type: "Full-Time", amount: "₹4,200" },
                                    { name: "Jane Smith", type: "Contract", amount: "₹3,150" },
                                    { name: "Mike Ross", type: "Full-Time", amount: "₹5,000" },
                                ].map((p, i) => (
                                    <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-slate-500/10 dark:bg-white/5">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 bg-indigo-100 dark:bg-indigo-900 rounded-full flex items-center justify-center text-indigo-600 dark:text-indigo-400 font-bold text-xs">{p.name[0]}</div>
                                            <div>
                                                <div className="text-sm font-bold dark:text-white">{p.name}</div>
                                                <div className="text-[10px] text-secondary dark:text-gray-400 uppercase tracking-wider">{p.type}</div>
                                            </div>
                                        </div>
                                        <div className="text-sm font-bold dark:text-white">{p.amount}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Why Payroll? */}
            <section className="py-24">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                        {[
                            { title: "Direct Deposit", icon: <CreditCard className="text-blue-500" />, desc: "Pay your team directly to their accounts with zero delay." },
                            { title: "Auto Tax Filings", icon: <Shield className="text-indigo-500" />, desc: "We calculate and file local, state, and federal taxes automatically." },
                            { title: "Time Tracking", icon: <Clock className="text-emerald-500" />, desc: "Sync timesheets directly into payroll with one click." },
                        ].map((item, i) => (
                            <div key={i} className="text-center">
                                <div className="p-4 bg-indigo-500/10 dark:bg-indigo-500/20 rounded-2xl w-fit mx-auto mb-6">
                                    {item.icon}
                                </div>
                                <h3 className="text-xl font-bold mb-2 dark:text-white uppercase tracking-tight">{item.title}</h3>
                                <p className="text-secondary dark:text-gray-400 leading-relaxed text-sm">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-20 px-4">
                <div className="max-w-4xl mx-auto bg-indigo-600 dark:bg-accent rounded-3xl p-12 text-center text-white relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-[80px] -mr-32 -mt-32"></div>
                    <h2 className="text-3xl md:text-5xl font-bold mb-6 relative z-10">Payroll made perfect</h2>
                    <p className="text-indigo-50 mb-8 text-lg relative z-10">Run your first payroll in less than 5 minutes.</p>
                    <button className="px-10 py-4 bg-white text-indigo-600 dark:text-accent rounded-xl font-bold hover:bg-opacity-90 transition-all flex items-center gap-2 mx-auto relative z-10 shadow-xl">
                        Start for Free <ArrowRight className="w-5 h-5" />
                    </button>
                </div>
            </section>
        </div>
    );
}
